//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React, { useState } from 'react';
import styled from 'styled-components';

import { CuralateCatalogProduct } from '@apis/universal-image-actions/universalImageActionsApiModel';
import { Alert } from '@components/atoms/alert/Alert';
import {
  PrimaryButton,
  PrimaryLinkButton,
} from '@components/atoms/button/Button';
import { SubsectionText } from '@components/atoms/text/Text';
import TextAreaInput from '@components/atoms/text-area-input/TextAreaInput';
import { Product } from '@models/IItemMetadata';
import { UiaProductTrackingIds } from '@uia/products/constants/UiaProductConstants';
import { useUpdateProductPdpUrlMutation } from '@uia/products/hooks/useUpdateProductPdpUrlMutation';
import { isDefined, isEmptyValue } from '@utils/filterPredicates';

interface Props {
  onCancel: () => void;
  onSave: () => void;
  product: Product | CuralateCatalogProduct;
}

export const BvProductEditor = ({ onCancel, onSave, product }: Props) => {
  const {
    isError,
    isLoading,
    mutate: updatePdpUrl,
  } = useUpdateProductPdpUrlMutation();

  const [pdpUrl, setPdpUrl] = useState(product.destinationUrl);

  const handleUpdateUrl = () => {
    // Get the external ID from the productStyleId, everything that is after the first colon
    // Example: bv_democlientx:test-id -> test-id
    const externalId = product.productStyleId?.split(':')[1];

    if (!isDefined(externalId) || !isDefined(pdpUrl)) {
      return;
    }
    updatePdpUrl(
      { externalId, pdpUrl },
      {
        onSuccess: () => {
          onSave();
        },
      },
    );
  };

  return (
    <RootContainer>
      <SubsectionText>{product.name ?? 'Name not found'}</SubsectionText>
      {isError && (
        <CompactAlert
          trackingId={UiaProductTrackingIds.PRODUCT_EDITOR_ERROR_ALERT}
          type='error'
        >
          Failed to update PDP URL. Try again or contact Bazaarvoice Support.
        </CompactAlert>
      )}
      <CompactAlert
        trackingId={UiaProductTrackingIds.PRODUCT_EDITOR_WARNING_ALERT}
        type='warning'
      >
        The PDP URL may take up to 5 minutes to reflect recent updates.
      </CompactAlert>
      <TextAreaInput
        hasError={isEmptyValue(pdpUrl) || isError}
        label='PDP URL'
        onChange={(value) => setPdpUrl(value)}
        placeholder='A destination URL is required.'
        rows={2}
        trackingId={UiaProductTrackingIds.PRODUCT_EDITOR_PDP_URL_TEXT_INPUT}
        value={pdpUrl}
      />
      <ButtonsContainer>
        <PrimaryLinkButton
          onClick={onCancel}
          trackingId={UiaProductTrackingIds.PRODUCT_EDITOR_CANCEL_BUTTON}
        >
          Cancel
        </PrimaryLinkButton>
        <PrimaryButton
          isDisabled={
            isEmptyValue(pdpUrl) ||
            pdpUrl === product.destinationUrl ||
            isLoading
          }
          onClick={handleUpdateUrl}
          trackingId={UiaProductTrackingIds.PRODUCT_EDITOR_UPDATE_URL_BUTTON}
        >
          Update URL
        </PrimaryButton>
      </ButtonsContainer>
    </RootContainer>
  );
};

const RootContainer = styled.div(
  ({ theme }) => `
    display: flex;
    flex-direction: column;
    gap: ${theme.spacings[3]};
  `,
);

const CompactAlert = styled(Alert)(
  ({ theme }) => `
  padding-top: ${theme.spacings[2]};
  padding-bottom: ${theme.spacings[2]};
  margin-bottom: ${theme.spacings[0]};
`,
);

const ButtonsContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: end;
  gap: ${theme.spacings[3]};
`,
);
